/*
  Price-per-mL sort for collection pages.
  - The sort <select> gets two extra options (ppml-ascending / ppml-descending) from
    snippets/ppml-sort-options.liquid.
  - Shopify can't sort by a metafield server-side, so when one of those options is chosen we:
      1. ask the server for the normal price sort (a sensible fallback ordering),
      2. tag the URL with ?ppml=asc|desc,
      3. fetch every page of the grid via the Section Rendering API, merge the cards,
         sort them by .price__ppml[data-ppml] (cents per mL, from snippets/price.liquid)
         and render them as one list, hiding pagination.
  - Cards without a per-mL price (non e-liquids) are kept at the end in their original order.
*/
(function () {
  const PARAM = 'ppml';
  const MAX_PAGES = 12;
  let busy = false;

  function currentMode() {
    return new URLSearchParams(window.location.search).get(PARAM);
  }

  function gridEl() {
    return document.getElementById('product-grid');
  }

  function ppmlOf(li) {
    const el = li.querySelector('.price__ppml[data-ppml]');
    if (!el) return null;
    const v = parseFloat(el.dataset.ppml);
    return isNaN(v) ? null : v;
  }

  function syncSelects(mode) {
    document.querySelectorAll('select[name="sort_by"]').forEach((sel) => {
      const want = mode === 'asc' ? 'ppml-ascending' : mode === 'desc' ? 'ppml-descending' : null;
      if (!want) return;
      const opt = Array.from(sel.options).find((o) => o.value === want);
      if (opt) sel.value = want;
    });
  }

  function pageCount(container) {
    let max = 1;
    container.querySelectorAll('.pagination-wrapper a[href*="page="]').forEach((a) => {
      const m = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return Math.min(max, MAX_PAGES);
  }

  async function applySort(mode) {
    const grid = gridEl();
    if (!grid || busy) return;
    const container = document.getElementById('ProductGridContainer');
    const sectionId = grid.dataset.id;
    const pages = pageCount(container || document);
    busy = true;
    grid.classList.add('ppml-sorting');

    try {
      let items = Array.from(grid.querySelectorAll(':scope > li.grid__item'));
      if (pages > 1) {
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.delete(PARAM);
        const fetches = [];
        for (let p = 1; p <= pages; p++) {
          const q = new URLSearchParams(params);
          q.set('page', String(p));
          q.set('section_id', sectionId);
          fetches.push(
            fetch(`${window.location.pathname}?${q.toString()}`)
              .then((r) => r.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                return Array.from(doc.querySelectorAll('#product-grid > li.grid__item'));
              })
              .catch(() => [])
          );
        }
        const all = (await Promise.all(fetches)).flat();
        if (all.length) items = all;
      }

      const withPrice = items.filter((li) => ppmlOf(li) !== null);
      const without = items.filter((li) => ppmlOf(li) === null);
      withPrice.sort((a, b) => (mode === 'desc' ? ppmlOf(b) - ppmlOf(a) : ppmlOf(a) - ppmlOf(b)));

      grid.innerHTML = '';
      [...withPrice, ...without].forEach((li, i) => {
        li.style.removeProperty('--animation-order');
        li.classList.remove('scroll-trigger', 'animate--slide-in');
        grid.appendChild(li);
      });
      const pag = (container || document).querySelector('.pagination-wrapper');
      if (pag) pag.style.display = 'none';
      const count = document.getElementById('ProductCount');
      if (count) count.textContent = `${items.length} products, sorted by price per mL`;
      syncSelects(mode);
    } finally {
      grid.classList.remove('ppml-sorting');
      busy = false;
    }
  }

  // Intercept the sort select before facets.js sees it.
  document.addEventListener(
    'input',
    (event) => {
      const sel = event.target;
      if (!(sel instanceof HTMLSelectElement) || sel.name !== 'sort_by') return;
      const chosen = sel.options[sel.selectedIndex];
      const mode = chosen && chosen.dataset.ppmlSort;
      const form = sel.closest('form');
      if (!mode) {
        // Leaving ppml mode: let facets.js handle it, but strip our param first.
        if (currentMode()) {
          const url = new URL(window.location.href);
          url.searchParams.delete(PARAM);
          history.replaceState(history.state, '', url.toString());
          if (window.FacetFiltersForm) FacetFiltersForm.searchParamsPrev = url.searchParams.toString();
        }
        return;
      }
      event.stopImmediatePropagation();
      event.preventDefault();
      if (!form || !window.FacetFiltersForm) return;
      const params = new URLSearchParams(new FormData(form));
      params.set('sort_by', mode === 'desc' ? 'price-descending' : 'price-ascending');
      params.delete('page');
      params.set(PARAM, mode);
      FacetFiltersForm.renderPage(params.toString(), event, true);
    },
    true
  );

  // Re-apply after facets.js re-renders the grid (filters, pagination, back/forward).
  const observe = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const obs = new MutationObserver(() => {
      const mode = currentMode();
      if (mode && !busy && gridEl() && !gridEl().classList.contains('ppml-sorting')) {
        // Debounce: facets.js swaps innerHTML once per render.
        clearTimeout(observe._t);
        observe._t = setTimeout(() => applySort(mode), 50);
      }
    });
    obs.observe(container, { childList: true, subtree: false });
  };

  document.addEventListener('DOMContentLoaded', () => {
    observe();
    const mode = currentMode();
    if (mode) applySort(mode);
  });
})();
